import React, {useContext, useEffect, useRef, useState} from 'react';
import {getAccountRatingCounts, getReviewFeed} from '../../../util/requestSender';
import {AppContext} from '../../misc/appContext';
import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import ReviewFeed from './reviewFeed';
import {createErrorMessage, setsAreEqual} from '../../../util/utils';
import {userHasRight} from '../../../util/accessUtil';
import {AccessKey} from '../../../util/interfaces';
import {Rating} from '../../../util/types';
import ProductReview from '../../../models/productReviewModel';
import {LAST_REVIEW_FLEX_KEY} from '../../../util/constants';
import {DefaultNumberObject} from '../../../util/defaultObject';
import {ReviewCountsRef} from '../reviewCounts/reviewCounts';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const LIMIT = 20;

function getInitialFilter(): Set<Rating> {
    const filter = new URLSearchParams(location.search).getAll('filter');
    const initialFilter: Set<Rating> = new Set();
    for (const value of filter) {
        const numFilter = Number(value);
        if (isNaN(numFilter)) continue;
        if (numFilter <= 5 && numFilter >= 1) initialFilter.add(numFilter as Rating);
    }
    return initialFilter;
}

type LoadParams = {scrollToTop?: boolean; saveTotal?: boolean; ratingsFilter?: Set<Rating>};

export default function ReviewFeedController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [reviews, setReviews] = useState<Array<ProductReview>>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [ratingCounts, setRatingCounts] = useState(new DefaultNumberObject<Rating>(undefined, () => -1));
    const [ratingsFilter, setRatingsFilter] = useState<Set<Rating>>(getInitialFilter());
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterTotal, setFilterTotal] = useState(0);
    const page = useRef(0);
    const filterRef = useRef<ReviewCountsRef>(null);

    function saveLastSeen() {
        const {account} = context.accountContext;
        if (!account) return;
        account.setFlex(LAST_REVIEW_FLEX_KEY, IDate.now().format());
        account.update();
    }

    function onOpenFilter() {
        setFilterOpen(true);
    }

    function onCloseFilter() {
        setFilterOpen(false);
        const selectedRatings = filterRef.current?.getCurrentSelected();
        if (selectedRatings === undefined) return;
        setRatingsFilter(selectedRatings);
        loadReviewFeed({scrollToTop: true, saveTotal: false, ratingsFilter: selectedRatings});
    }

    function onClearRatingFilter() {
        setRatingsFilter(new Set());
        loadReviewFeed({saveTotal: false, scrollToTop: true, ratingsFilter: new Set()});
    }

    function onReviewStatClick(evt: CustomEvent<'all' | 'positive' | 'negative'>) {
        const stat = evt.detail;
        let ratings: Set<Rating> = new Set();
        if (stat === 'positive') ratings = new Set([4, 5]);
        else if (stat === 'negative') ratings = new Set([1, 2, 3]);
        if (setsAreEqual(ratings, ratingsFilter)) return;
        setReviews([]);
        window.setTimeout(() => {
            setRatingsFilter(ratings);
            loadReviewFeed({saveTotal: false, scrollToTop: true, ratingsFilter: ratings});
        }, 1);
    }

    function onLoadMore(callback: VoidFunction) {
        if (!account) return;
        page.current += 1;
        setLoadingMore(true);
        getReviewFeed({
            data: {
                account_id: account.id,
                page: page.current,
                limit: LIMIT,
                filter_ratings: Array.from(ratingsFilter)
            },
            callback: (data: ResponseData) => {
                setLoadingMore(false);
                if (data.success) {
                    const _reviews: Array<ProductReview> = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(reviews.concat(_reviews));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('reviewAnalytics.reviewFeed.loadFeedFailed', 'Het laden van de feed is niet gelukt.')
                        ),
                        t('reviewAnalytics.reviewFeed.loadFailed', 'Laden mislukt')
                    );
                window.setTimeout(callback);
            }
        });
    }

    function loadReviewFeed(params?: LoadParams) {
        if (!account) return;
        setLoading(true);
        const _ratingsFilter = params?.ratingsFilter ?? ratingsFilter;
        const saveTotal = params?.saveTotal ?? true;
        const scrollToTop = params?.scrollToTop ?? false;
        getReviewFeed({
            data: {account_id: account.id, page: 0, limit: LIMIT, filter_ratings: Array.from(_ratingsFilter)},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const _reviews: Array<ProductReview> = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(_reviews);
                    if (saveTotal) setFilterTotal(data.total);
                    setTotal(data.total);
                    if (scrollToTop) window.scrollTo({top: 0, behavior: 'smooth'});
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('reviewAnalytics.reviewFeed.loadFeedFailed', 'Het laden van de feed is niet gelukt.')
                        ),
                        t('reviewAnalytics.reviewFeed.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    function loadRatingCounts() {
        if (!account) return;
        getAccountRatingCounts({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) {
                    const {rating_counts} = data;
                    setRatingCounts(
                        new DefaultNumberObject<Rating>(
                            {
                                1: rating_counts.rating_one,
                                2: rating_counts.rating_two,
                                3: rating_counts.rating_three,
                                4: rating_counts.rating_four,
                                5: rating_counts.rating_five
                            },
                            () => 0
                        )
                    );
                }
            }
        });
    }

    useEffect(() => {
        if (!userHasRight(context.accountContext.account, AccessKey.RATING_VIEW)) return;
        loadReviewFeed();
        saveLastSeen();
        loadRatingCounts();
    }, [account?.id]);

    useEffect(() => {
        if (filterOpen)
            window.setTimeout(() => {
                document.body.addEventListener('click', onCloseFilter);
            });
        else document.body.removeEventListener('click', onCloseFilter);
        return function () {
            document.body.removeEventListener('click', onCloseFilter);
        };
    }, [filterOpen, onCloseFilter]);

    useEffect(() => {
        document.addEventListener('reviewStatClick', onReviewStatClick);

        return function () {
            document.removeEventListener('reviewStatClick', onReviewStatClick);
        };
    }, [ratingsFilter]);

    return (
        <ReviewFeed
            onLoadMore={onLoadMore}
            onOpenFilter={onOpenFilter}
            onCloseFilter={onCloseFilter}
            onClearRatingFilter={onClearRatingFilter}
            reviews={reviews}
            loading={loading}
            loadingMore={loadingMore}
            total={total}
            ratingCounts={ratingCounts}
            ratingsFilter={ratingsFilter}
            filterOpen={filterOpen}
            filterTotal={filterTotal}
            filterRef={filterRef}
        />
    );
}
